import {Injectable} from "@angular/core";

import {Page} from "../../src/app/shared/sdk/models/Page";
import {PageApi} from "../../src/app/shared/sdk/services/custom/Page";
import {Article} from "../../src/app/shared/sdk/models/Article";
import {ArticleApi} from "../../src/app/shared/sdk/services/custom/Article";
import {StringUtils} from "../../src/app/shared/services/stringutils";
const PageUtils = require("./pageutils.js");

@Injectable()
export class ArticleUtils {

  constructor(protected pageApi: PageApi, protected articleApi: ArticleApi, protected stringUtils: StringUtils) {}

  public loadArticle(url: string) {
    return this.pageApi.findOne({where: {url: url}, include: 'pageable'})
      .map((page: Page) => { return this.composeArticlePage(page) });
  }

  public loadPreviousArticle(publishDate, offset) {
    return this.pageApi.find({
      where: {
          publishDate: {lt: publishDate},
          pageType: 'Article',
          published: true,
          url: {regexp: '/articles/'}
      },
      include: 'pageable',
      order: 'publishDate DESC',
      limit: 1,
      skip: offset
    })
      .map((pages: Page[]) => {
        return pages.map((page: Page) => { return this.composeArticlePage(page) })
                    .filter(element => { return element !== undefined; });
      });
  }

  public loadLatestArticle() {
    return this.pageApi.find({
      where: {
        pageType: 'Article',
        published: true,
        url: {regexp: '/articles/'}
      },
      include: 'pageable',
      order: 'publishDate DESC',
      limit: 1,
      skip: 0
    })
      .map((pages: Page[]) => {
        return pages.map((page: Page) => { return this.composeArticlePage(page)})
          .filter(element => { return element !== undefined; });
      });
  }

  public loadLatestFeaturedArticle() {
    return this.pageApi.find({
      where: {
        pageType: 'Article',
        published: true,
        featuredInHome: true,
        url: {regexp: '/articles/'}
      },
      include: 'pageable',
      order: 'publishDate DESC',
      limit: 1,
      skip: 0
    })
      .map((pages: Page[]) => {
        return pages.map((page: Page) => { return this.composeArticlePage(page)})
          .filter(element => { return element !== undefined; });
      });
  }

  protected composeArticlePage(page:Page) {

    if (page != null && page.pageType == 'Article' && !!page.pageable) {

      let article = page.pageable;

      let readablePublishDate = PageUtils.getReadablePublishDate(page);

      let shareCopy = null;
      if (!!article.shareCopy && article.shareCopy.trim().length > 0) {
        shareCopy = article.shareCopy;
      }

      //replace every http by https within the article content
      if (!!article.content && article.content.length > 0) {
        article.content = article.content.replace(/http\:\/\/cdn/g, 'https://cdn');
      }

      let ogImageWithProtocol = null; //todo
      let imagesOnContent = PageUtils.getAllImagesOnContent(article.content);
      let embeddedTweetHtml = null; //todo

      let articleData = {
        id: article.id,
        title: article.title,
        content: this.stringUtils.escapeTransferStateCacheData(article.content),
        url: page.url,
        twitterText: page.twitterText,
        ogTitle: page.ogTitle || page.metaTitle || article.title,
        ogDescription: page.ogDescription || page.metaDescription,
        publishDate: page.publishDate,
        readableDate: readablePublishDate,
        author: page.author,
        view: (!!page.view) ? page.view: null,
        firstName: (!!page.author) ? page.author.split(' ')[0].toLowerCase() : null,
        ogImage: ogImageWithProtocol,
        ogImageUrls: imagesOnContent,
        pageUrl: page.url,
        canonicalUrl: "https://0.0.0.0:8081" + +page.url,
        pageMetaTitle: page.metaTitle || article.title,
        pageMetaDescription: page.metaDescription || page.ogDescription,
        pageId: page.id,
        shareCopy: shareCopy,
        embeddedTweetHtml: embeddedTweetHtml,
        thumbnail: page.thumbnail,
        unbrandedImage: this.composeUnbrandedImage(page),
        category: article.category,
        categoryId: article.categoryId,
        tag: article.tag,
        excerpt: article.excerpt,
        published: page.published,
        featuredInHome: page.featuredInHome
      };

      return articleData;
    }
    else {
      //redirect to new page...
    }

    return null;
  }

  public loadSectionArticles(categoryId: string, itemsToLoad: number, idArticlesToExclude: Array<string>) {
    return this.articleApi.find({
      where: {
        categoryId: categoryId,
        id: {nin: idArticlesToExclude}
      },
      include: 'page',
      order: 'created DESC',
      limit: (itemsToLoad * 2),
    })
      .map((articles: Article[]) => {

        return articles.map((article: Article) => {

          let page: Page = article.page;
          article.page = null;
          page.pageable = article;

          return this.composeArticlePage(page);
        }).filter(element => { return element['published']; }).slice(0, itemsToLoad);
      });
  }

  public loadPreviousArticles(offset: number, limit: number, idArticlesToExclude: Array<string>) {
    return this.articleApi.find({
      where: {
        categoryId: {exists: true},
        id: {nin: idArticlesToExclude}
      },
      include: 'page',
      order: 'created DESC',
      limit: limit,
      skip: offset
    })
      .map((articles: Article[]) => {
        return articles.map((article: Article) => {

          let page: Page = article.page;
          article.page = null;
          page.pageable = article;

          return this.composeArticlePage(page);
        }).filter(element => { return element['published']; });
      });
  }

  public loadPreviousArticlesByCategory(categoryId: string, offset: number, limit: number) {

    return this.articleApi.find({
      where: {
        categoryId: categoryId,
      },
      include: 'page',
      order: 'created DESC',
      limit: limit,
      skip: offset
    }).map((articles: Article[]) => {

      return articles.map((article: Article) => {

        let page: Page = article.page;
        article.page = null;
        page.pageable = article;

        return this.composeArticlePage(page);
      }).filter(element => { return element['published']; });
    });
  }

  protected composeUnbrandedImage(page) {
    let unbrandedImage = null;

    if(!!page.unbrandedImage && page.unbrandedImage.length > 0) {
      unbrandedImage = page.unbrandedImage;
    } else {

      if(!!page.thumbnail && page.thumbnail.length > 0) {
        unbrandedImage = page.thumbnail.replace(/content\/images\//,'content/images/1600/');
      }

      if(unbrandedImage == null) {
        unbrandedImage = page.thumbnail;
      }
    }

    return unbrandedImage;
  }

}



// WEBPACK FOOTER //
// ./common/utils/articleutils.ts